import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Calendar, BarChart3 } from 'lucide-react';

const RateAnalysis = () => {
    const [fromDate, setFromDate] = useState('2026-04-01');
    const [toDate, setToDate] = useState('2026-05-30');

    const RATES = [
        { lane: 'CNSHA → USLAX', teu: '(LMS TEU)', contract: 1090, spot: 2241, change: -17.4, save: true },
        { lane: 'NLRTM → USNYC', teu: '(LMS TEU)', contract: 1450, spot: 1700, change: -16.1, save: true },
        { lane: 'KRPUS → USLAX', teu: '(LMS TEU)', contract: 1860, spot: 1700, change: 10.5, save: false },
        { lane: 'INBOM → DEHAM', teu: '(LMS TEU)', contract: 2350, spot: 2050, change: 14.2, save: true },
        { lane: 'CNNGB → GBFXT', teu: '(LMS TEU)', contract: 1860, spot: 1820, change: 3.2, save: false },
    ];

    const max = Math.max(...RATES.flatMap((r) => [r.contract, r.spot]));

    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="rounded-2xl bg-white dark:bg-slate-900 border border-ink-100 dark:border-slate-800 shadow-card overflow-hidden h-full flex flex-col"
            data-testid="rate-analysis"
        >
            <div className="px-5 py-4 border-b border-ink-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white flex items-center justify-center shrink-0">
                        <BarChart3 className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[14px] font-semibold text-ink-900 dark:text-white">Rate Analysis</div>
                        <div className="text-[12px] text-ink-500 dark:text-slate-400">Contract vs Spot rate comparison</div>
                    </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-2 flex-wrap">
                    <div className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-ink-200 dark:border-slate-700 text-[12px] text-ink-700 dark:text-slate-300 bg-white dark:bg-slate-800">
                        <Calendar className="w-3.5 h-3.5 text-ink-500 dark:text-slate-400 shrink-0" />
                        <span className="whitespace-nowrap">01-04-2026 — 30-05-2026</span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px]">
                        <span className="inline-flex items-center gap-1.5 text-ink-600 dark:text-slate-400">
                            <span className="w-2.5 h-2.5 rounded-sm bg-blue-500" />
                            <span className="hidden xs:inline">Contract</span>
                            <span className="xs:hidden">C</span>
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-ink-600 dark:text-slate-400">
                            <span className="w-2.5 h-2.5 rounded-sm bg-amber-500" />
                            <span className="hidden xs:inline">Spot</span>
                            <span className="xs:hidden">S</span>
                        </span>
                    </div>
                </div>
            </div>

            <div className="p-5 flex-1 space-y-4">
                {RATES.map((r, idx) => (
                    <div key={r.lane}>
                        <div className="flex items-center justify-between text-[12.5px]">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-ink-900 dark:text-white">{r.lane}</span>
                                <span className="text-[10.5px] text-ink-500 dark:text-slate-400">{r.teu}</span>
                            </div>
                            <span className={[
                                'inline-flex items-center gap-1 text-[11.5px] font-semibold',
                                r.save ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
                            ].join(' ')}>
                                {r.save ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
                                {r.save ? 'Save' : 'Over'} {Math.abs(r.change)}%
                            </span>
                        </div>
                        <div className="mt-2 space-y-1.5">
                            <div className="flex items-center gap-2">
                                <span className="w-14 text-[10px] uppercase tracking-wider text-ink-500 dark:text-slate-400">Contract</span>
                                <div className="relative flex-1 h-3 rounded-md bg-ink-100 dark:bg-slate-800 overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(r.contract / max) * 100}%` }}
                                        transition={{ duration: 0.6, delay: idx * 0.05 }}
                                        className="absolute inset-y-0 left-0 rounded-md bg-blue-500"
                                    />
                                </div>
                                <span className="w-16 text-right text-[12px] font-semibold text-ink-900 dark:text-white tabular-nums">${r.contract}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-14 text-[10px] uppercase tracking-wider text-ink-500 dark:text-slate-400">Spot</span>
                                <div className="relative flex-1 h-3 rounded-md bg-ink-100 dark:bg-slate-800 overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(r.spot / max) * 100}%` }}
                                        transition={{ duration: 0.6, delay: idx * 0.05 + 0.1 }}
                                        className="absolute inset-y-0 left-0 rounded-md bg-amber-500"
                                    />
                                </div>
                                <span className="w-16 text-right text-[12px] font-semibold text-ink-900 dark:text-white tabular-nums">${r.spot}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </motion.div>
    );
};

export default RateAnalysis;